import { getContentStorageUrl, isAdminOrTest } from 'helpers';
import { audioJournalArticle, audioJournalId, canListen, editionIdOf, timeMs } from 'helpers/audioJournal';
import { createSelector } from 'reselect';
import { ApplicationState, Article, ArticlesById, ContentType, Edition } from 'types';

export const isPrivileged = (state: ApplicationState) => isAdminOrTest(state);

const getEditions = (state: ApplicationState) => state.filteredDataState.editions;

const releasedAt = (edition: Edition) => timeMs(edition.audio?.publishTime) ?? timeMs(edition.publishTime) ?? 0;

/** Issues whose audio this user may hear (drafts and scheduled audio only for admin/test users), newest first. */
export const getListenableEditions = createSelector([getEditions, isPrivileged], (editions, privileged) =>
	editions.filter((edition) => !edition.deleted && canListen(edition, privileged)).sort((a, b) => releasedAt(b) - releasedAt(a))
);

export const getLatestAudioPerPublication = createSelector([getListenableEditions], (editions) => {
	const seen = new Set<string>();
	return editions.filter((edition) => {
		if (seen.has(edition.publicationId)) {
			return false;
		}
		seen.add(edition.publicationId);
		return true;
	});
});

export function audioJournalArticleForEdition(state: ApplicationState, edition?: Edition): Article | undefined {
	if (!edition || edition.deleted || !canListen(edition, isPrivileged(state))) {
		return undefined;
	}
	const publication = state.dataState.publications.byId[edition.publicationId];
	return audioJournalArticle(edition, publication, getContentStorageUrl(ContentType.Edition, 'cover', edition));
}

/** The player item for an `aj-{editionId}` id, if the issue still has audio this user may hear. */
export function audioJournalArticleFor(state: ApplicationState, id: string): Article | undefined {
	return audioJournalArticleForEdition(state, state.dataState.editions.byId[editionIdOf(id)]);
}

const getPublicationsById = (state: ApplicationState) => state.dataState.publications.byId;
const toArticle = (edition: Edition, publications: ApplicationState['dataState']['publications']['byId']) =>
	audioJournalArticle(edition, publications[edition.publicationId], getContentStorageUrl(ContentType.Edition, 'cover', edition));

/** Player items for every issue this user may hear, by item id (`aj-{editionId}`). */
export const getAudioJournalArticles = createSelector([getListenableEditions, getPublicationsById], (editions, publications) =>
	editions.reduce((byId, edition) => ({ ...byId, [audioJournalId(edition.id)]: toArticle(edition, publications) }), {} as ArticlesById)
);

/** The newest issue with audio of each publication, as items for the usual article lists. */
export const getLatestAudioJournalArticles = createSelector([getLatestAudioPerPublication, getPublicationsById], (editions, publications) =>
	editions.map((edition) => toArticle(edition, publications))
);
